import { create } from "zustand";
import type { Survey } from "../survey/schema";
import { type Answer, loadFromStorage, saveToStorage, clearStorage } from "./persistence";

export type Status = "idle" | "loading" | "submitting" | "submitted" | "error";

type State = {
  survey: Survey | null;
  answers: Record<string, Answer>;
  status: Status;
  errorMessage: string | null;
};

type Actions = {
  setSurvey: (survey: Survey) => void;
  setAnswer: (questionId: string, value: number | string | null, transcript: string | null) => void;
  setTranscript: (questionId: string, transcript: string) => void;
  setStatus: (status: Status, errorMessage?: string | null) => void;
  clear: () => void;
};

export const useSurveyStore = create<State & Actions>((set, get) => ({
  survey: null,
  answers: {},
  status: "idle",
  errorMessage: null,

  setSurvey: (survey) => {
    const persisted = loadFromStorage(survey.id) ?? {};
    set({ survey, answers: persisted, status: "idle", errorMessage: null });
  },

  setAnswer: (questionId, value, transcript) => {
    const survey = get().survey;
    if (!survey) return;
    const next: Answer = {
      questionId,
      value,
      transcript,
      updatedAt: Date.now(),
      skipped: false,
    };
    const answers = { ...get().answers, [questionId]: next };
    set({ answers });
    saveToStorage(survey.id, answers);
  },

  setTranscript: (questionId, transcript) => {
    const survey = get().survey;
    if (!survey) return;
    const prev = get().answers[questionId];
    const next: Answer = {
      questionId,
      value: prev?.value ?? null,
      transcript,
      updatedAt: Date.now(),
    };
    const answers = { ...get().answers, [questionId]: next };
    set({ answers });
    saveToStorage(survey.id, answers);
  },

  setStatus: (status, errorMessage = null) => set({ status, errorMessage }),

  clear: () => {
    const id = get().survey?.id;
    if (id) clearStorage(id);
    set({ survey: null, answers: {}, status: "idle", errorMessage: null });
  },
}));
